import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Tabs, Modal, Form, Select, DatePicker, Input, Tooltip, Spin, Dropdown,
} from 'antd';
import {
  FileExcelOutlined, FileWordOutlined, PlusOutlined, ArrowLeftOutlined,
  CheckOutlined, ReloadOutlined, PrinterOutlined, AlertOutlined, LoadingOutlined,
  DeleteOutlined, BankOutlined, MoreOutlined, EditOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import bilateralesService from '../../services/bilateralesService';
import { formatDate } from '../../variables/constants';
import DetailsSession from './DetailsSession';
import PreChecklistModal from './components/PreChecklistModal';
import './_bilateral-style.css';
import { filtreSansAccents } from '../../utils/rechercheTexte';

import { useTranslation, Trans } from 'react-i18next';
import { localeIntl } from '../../i18n/format';
import { avecLibelleTraduit, libellesTraduits } from '../../i18n/libelles';

const G = '#1a6b3c';
const INK = '#0f172a';
const MUTED = '#94a3b8';
const BORDER = '#e2e8f0';
const WHITE = '#ffffff';

const STATUT_COLOR = avecLibelleTraduit({
  PLANIFIEE: { bg: '#dbeafe', color: '#1e3a8a' },
  EN_COURS: { bg: '#ffedd5', color: '#9a3412' },
  CLOTUREE: { bg: '#e2e8f0', color: '#475569' },
}, 'bilaterales:statuts');

const DIRECTION_LABEL = libellesTraduits('bilaterales:rencontre.directions');

const fmt = (v) => !v && v !== 0 ? '-' : new Intl.NumberFormat(localeIntl()).format(Number(v));

export default function DetailsRencontre() {
  const { t } = useTranslation('bilaterales');
  const { id } = useParams();
  const navigate = useNavigate();
  const [rb, setRb] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [compagnies, setCompagnies] = useState([]);
  const [activeTab, setActiveTab] = useState();
  const [addSessionOpen, setAddSessionOpen] = useState(false);
  const [sessionForm] = Form.useForm();
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [bureaux, setBureaux] = useState([]);
  const [preChecklistOpen, setPreChecklistOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  // Compagnie choisie dans le modal de programmation - pilote les indicateurs
  // dynamiques (calendrier + rappel « déjà programmée »).
  const compagnieChoisie = Form.useWatch('compagnieTrackingId', sessionForm);
  const dateChoisie = Form.useWatch('dateSession', sessionForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bilateralesService.get(id);
      setRb(data);
      const ses = await bilateralesService.listSessions(id);
      setSessions(ses || []);
      setActiveTab((prev) => prev || ses?.[0]?.scTrackingId);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    axiosInstance.get('/v1/organismes/type/COMPAGNIE_MEMBRE')
      .then((res) => setCompagnies(res.data?.data || []))
      .catch(() => { });
    axiosInstance.get('/v1/organismes/type/BUREAU_HOMOLOGUE')
      .then((res) => setBureaux(res.data?.data || []))
      .catch(() => { });
  }, []);

  if (loading || !rb) {
    return (
      <Layout title={t('rencontre.titreChargement')}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: G }} />} />
        </div>
      </Layout>
    );
  }

  const ajouterSession = async (vals) => {
    try {
      await bilateralesService.addSession(id, {
        compagnieTrackingId: vals.compagnieTrackingId,
        dateSession: vals.dateSession.format('YYYY-MM-DD'),
        observations: vals.observations,
      });
      toast.success(t('rencontre.programmation.succes'));
      setAddSessionOpen(false);
      sessionForm.resetFields();
      setRefreshKey((k) => k + 1);
    } catch (_) { }
  };

  // Modification des informations - autorisée uniquement tant que la rencontre est PLANIFIÉE.
  const ouvrirEdition = () => {
    editForm.setFieldsValue({
      direction: rb.direction,
      bureauHomologueTrackingId: rb.bureauHomologueTrackingId,
      periode: rb.dateDebut && rb.dateFin ? [dayjs(rb.dateDebut), dayjs(rb.dateFin)] : undefined,
      observationsGenerales: rb.observationsGenerales,
    });
    setEditOpen(true);
  };

  const enregistrerEdition = async (vals) => {
    try {
      const bureau = bureaux.find((b) => b.organismeTrackingId === vals.bureauHomologueTrackingId);
      await bilateralesService.update(id, {
        direction: vals.direction,
        paysHomologueCode: bureau?.codePays || rb.paysHomologueCode,
        bureauHomologueTrackingId: vals.bureauHomologueTrackingId,
        dateDebut: vals.periode[0].format('YYYY-MM-DD'),
        dateFin: vals.periode[1].format('YYYY-MM-DD'),
        observationsGenerales: vals.observationsGenerales,
      });
      toast.success(t('rencontre.edition.succes'));
      setEditOpen(false);
      setRefreshKey((k) => k + 1);
    } catch (_) { /* erreur affichée par l'intercepteur */ }
  };

  const cloturer = () => {
    Modal.confirm({
      title: t('rencontre.cloture.titre'),
      content: t('rencontre.cloture.contenu'),
      okText: t('rencontre.cloture.cloturer'),
      okButtonProps: { danger: true },
      cancelText: t('actions.annuler'),
      onOk: async () => {
        await bilateralesService.changerStatut(id, 'CLOTUREE');
        toast.success(t('rencontre.cloture.succes'));
        setRefreshKey((k) => k + 1);
      },
    });
  };

  const supprimer = () => {
    Modal.confirm({
      title: t('suppression.titre', { reference: rb.reference }),
      content: (
        <div>
          <p><Trans t={t} i18nKey="suppression.irreversible" components={{ 1: <strong /> }} /></p>
          <ul style={{ paddingLeft: 18 }}>
            <li>{t('suppression.sessions', { n: rb.nbSessions || 0 })}</li>
            <li>{t('suppression.dossiers', { n: rb.nbDossiers || 0 })}</li>
            <li>{t('suppression.relances')}</li>
          </ul>
          <p style={{ color: '#ef4444' }}><Trans t={t} i18nKey="suppression.sinistresNonAffectes" components={{ 1: <strong /> }} /></p>
        </div>
      ),
      okText: t('suppression.definitivement'),
      okButtonProps: { danger: true },
      cancelText: t('actions.annuler'),
      onOk: async () => {
        await bilateralesService.supprimer(id);
        toast.success(t('suppression.succes'));
        navigate('/bilaterales');
      },
    });
  };

  const exportExcel = () =>
    bilateralesService.downloadExport(bilateralesService.exportExcelUrl(id), `${rb.reference}.xlsx`);
  const exportWord = () =>
    bilateralesService.downloadExport(bilateralesService.exportWordUrl(id), `PV-${rb.reference}.docx`);

  const stCol = STATUT_COLOR[rb.statut] || STATUT_COLOR.PLANIFIEE;

  // Sessions indexées par jour (YYYY-MM-DD) pour marquer le calendrier du modal.
  const sessionsParJour = sessions.reduce((acc, s) => {
    if (s.dateSession) (acc[s.dateSession] = acc[s.dateSession] || []).push(s);
    return acc;
  }, {});
  const sessionsCompagnieChoisie = compagnieChoisie
    ? sessions.filter((s) => s.compagnieTrackingId === compagnieChoisie)
    : [];

  // Cellule du calendrier : période de la rencontre en surbrillance, pastilles
  // sur les jours ayant déjà des compagnies programmées (orange = la compagnie
  // sélectionnée dans le formulaire).
  const renderCelluleJour = (current, info) => {
    if (info.type !== 'date') return info.originNode;
    const d = dayjs(current);
    const estSelectionne = dateChoisie && d.isSame(dateChoisie, 'day');
    // Le jour sélectionné garde le style plein d'antd : pas de fond clair par-dessus.
    const enPeriode = !estSelectionne && rb.dateDebut && rb.dateFin
      && !d.isBefore(dayjs(rb.dateDebut), 'day') && !d.isAfter(dayjs(rb.dateFin), 'day');
    const sessionsJour = sessionsParJour[d.format('YYYY-MM-DD')] || [];
    return (
      <div
        className="ant-picker-cell-inner"
        title={sessionsJour.length
          ? t('rencontre.programmation.dejaProgrammees', { compagnies: sessionsJour.map((s) => s.compagnieRaisonSociale).join(', ') })
          : undefined}
        style={{ position: 'relative', ...(enPeriode ? { background: '#e8f5ed', borderRadius: 6 } : null) }}
      >
        {d.date()}
        {sessionsJour.length > 0 && (
          <span style={{
            position: 'absolute', bottom: 0, left: '50%',
            transform: 'translateX(-50%)', display: 'flex', gap: 2
          }}>
            {sessionsJour.slice(0, 3).map((s) => (
              <span key={s.scTrackingId} style={{
                width: 5, height: 5, borderRadius: '50%',
                background: s.compagnieTrackingId === compagnieChoisie ? '#f97316' : G
              }} />
            ))}
          </span>
        )}
      </div>
    );
  };

  // Barre d'actions hiérarchisée : une seule action principale visible, le
  // reste (checklist, exports, suppression) regroupé dans le menu « Actions ».
  const actionsMenu = {
    items: [
      { key: 'checklist', icon: <AlertOutlined style={{ color: '#f97316' }} />, label: t('rencontre.menu.preChecklist'), onClick: () => setPreChecklistOpen(true) },
      { type: 'divider' },
      { key: 'excel', icon: <FileExcelOutlined style={{ color: '#107c41' }} />, label: t('rencontre.menu.exporterExcel'), onClick: exportExcel },
      { key: 'word', icon: <FileWordOutlined style={{ color: '#2b579a' }} />, label: t('rencontre.menu.exporterPv'), onClick: exportWord },
      { key: 'print', icon: <PrinterOutlined />, label: t('rencontre.menu.imprimer'), onClick: () => window.print() },
      { type: 'divider' },
      { key: 'delete', icon: <DeleteOutlined />, danger: true, label: t('rencontre.menu.supprimer'), onClick: supprimer },
    ],
  };

  return (
    <Layout title={t('rencontre.titre', { reference: rb.reference })}>
      <div className="bilat">

        {/* ── Bouton retour (aligné à gauche) ── */}
        <div className="no-print" style={{ marginBottom: 12 }}>
          <ButtonGhost icon={<ArrowLeftOutlined />} onClick={() => navigate('/bilaterales')}>
            {t('rencontre.retour')}
          </ButtonGhost>
        </div>

        {/* ── En-tête style EtatReclamations ── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: 16
        }}>
          <div>
            <div style={{
              fontSize: 11, fontWeight: 600, color: MUTED,
              letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 4
            }}>
              {t('rencontre.surtitre')}
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: INK, margin: 0 }}>
              {rb.reference}
              <span style={{
                marginLeft: 12, fontSize: 12, fontWeight: 600,
                background: stCol.bg, color: stCol.color, padding: '4px 10px',
                borderRadius: 12
              }}>
                {stCol.label}
              </span>
            </h1>
            <p style={{ margin: '4px 0 0', color: MUTED, fontSize: 13 }}>
              {rb.bureauHomologueRaisonSociale} ({rb.paysHomologueCode}) • {formatDate(rb.dateDebut)} → {formatDate(rb.dateFin)} • {DIRECTION_LABEL[rb.direction]}
            </p>
          </div>

          <div className="no-print" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tooltip title={t('rencontre.actualiser')}>
              <ButtonGhost icon={<ReloadOutlined />} onClick={() => setRefreshKey((k) => k + 1)} />
            </Tooltip>

            {/* Action principale unique selon l'état du cycle de vie */}
            {rb.statut === 'PLANIFIEE' ? (
              <Tooltip title={t('rencontre.modifierInfo')}>
                <button onClick={ouvrirEdition} style={btnPrimary}>
                  <EditOutlined /> {t('rencontre.modifier')}
                </button>
              </Tooltip>
            ) : rb.statut !== 'CLOTUREE' && (
              <Tooltip title={t('rencontre.cloture.reservee')}>
                <button onClick={cloturer} style={btnDanger}>
                  <CheckOutlined /> {t('rencontre.cloture.cloturer')}
                </button>
              </Tooltip>
            )}
            {rb.statut === 'PLANIFIEE' && (
              <Tooltip title={t('rencontre.cloture.reservee')}>
                <button onClick={cloturer} style={btnGhost}>
                  <CheckOutlined /> {t('rencontre.cloture.cloturer')}
                </button>
              </Tooltip>
            )}

            {/* Secondaire et destructeur regroupés */}
            <Dropdown menu={actionsMenu} trigger={['click']}>
              <button style={btnGhost} aria-label={t('rencontre.autresActions')}>
                <MoreOutlined /> {t('rencontre.actions')}
              </button>
            </Dropdown>
          </div>
        </div>

        {/* ── Contenu en deux onglets : chiffres d'un côté, travail compagnie de
             l'autre - évite l'empilement vertical de tous les blocs. ── */}
        <Tabs
          defaultActiveKey="compagnies"
          items={[
            {
              key: 'synthese',
              label: t('rencontre.onglets.synthese'),
              children: (
                <>
                  {/* Bande de métriques : valeurs à zéro estompées (pas de mur de zéros) */}
                  <div style={{
                    background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10,
                    padding: '12px 6px', marginBottom: 12, display: 'flex', alignItems: 'stretch',
                    flexWrap: 'wrap', rowGap: 12
                  }}>
                    {[
                      { label: t('champs.compagnies'), value: rb.nbSessions },
                      { label: t('champs.dossiers'), value: rb.nbDossiers },
                      { label: t('champs.reclame'), value: rb.totalMontantReclame, money: true },
                      { label: t('champs.bap'), value: rb.totalMontantBap, money: true },
                      { label: t('champs.fgTg'), value: rb.totalFgTg, money: true },
                      { label: t('champs.bapTotal'), value: rb.totalBapTotal, money: true, accent: true },
                    ].map((m, i) => (
                      <React.Fragment key={m.label}>
                        {i > 0 && <span style={{ width: 1, background: BORDER, margin: '2px 0' }} />}
                        <Metric {...m} />
                      </React.Fragment>
                    ))}
                  </div>

                  {/* État de la rencontre */}
                  <div style={{
                    background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10,
                    padding: '14px 18px', display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14
                  }}>
                    {[
                      { label: t('champs.bureauHomologue'), value: `${rb.bureauHomologueRaisonSociale} (${rb.paysHomologueCode})` },
                      { label: t('champs.periode'), value: `${formatDate(rb.dateDebut)} → ${formatDate(rb.dateFin)}` },
                      { label: t('champs.direction'), value: DIRECTION_LABEL[rb.direction] },
                      { label: t('champs.statut'), value: stCol.label },
                    ].map((f) => (
                      <div key={f.label}>
                        <div style={{
                          fontSize: 10.5, fontWeight: 600, color: MUTED,
                          letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 2
                        }}>
                          {f.label}
                        </div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: INK }}>{f.value}</div>
                      </div>
                    ))}
                    {rb.observationsGenerales && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{
                          fontSize: 10.5, fontWeight: 600, color: MUTED,
                          letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 2
                        }}>
                          {t('champs.observationsGenerales')}
                        </div>
                        <div style={{ fontSize: 13, color: INK, whiteSpace: 'pre-wrap' }}>
                          {rb.observationsGenerales}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ),
            },
            {
              key: 'compagnies',
              label: `${t('rencontre.onglets.compagnies')}${sessions.length ? ` (${sessions.length})` : ''}`,
              children: sessions.length === 0 ? (
                <div style={{
                  background: WHITE, border: '1px dashed #cbd5e1', borderRadius: 12,
                  padding: '40px 24px', textAlign: 'center'
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%', background: '#e8f5ed',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12
                  }}>
                    <BankOutlined style={{ fontSize: 20, color: G }} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 4 }}>
                    {t('rencontre.aucuneSession')}
                  </div>
                  <div style={{ fontSize: 13, color: MUTED, marginBottom: 18 }}>
                    {t('rencontre.aucuneSessionAide')}
                  </div>
                  {rb.statut !== 'CLOTUREE' && (
                    <button onClick={() => setAddSessionOpen(true)} style={btnPrimary}>
                      <PlusOutlined /> {t('rencontre.programmation.bouton')}
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                    {rb.statut !== 'CLOTUREE' && (
                      <button onClick={() => setAddSessionOpen(true)} style={btnGhost}>
                        <PlusOutlined /> {t('rencontre.programmation.bouton')}
                      </button>
                    )}
                  </div>
                  <Tabs
                    type="card"
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={sessions.map((s) => ({
                      key: s.scTrackingId,
                      label: (
                        <span>
                          {s.compagnieRaisonSociale}
                          <span style={{ color: MUTED, fontSize: 11, marginLeft: 6 }}>
                            {formatDate(s.dateSession)} · {t('rencontre.nbDossiers', { count: s.nbDossiers || 0 })}
                          </span>
                        </span>
                      ),
                      children: (
                        <DetailsSession
                          session={s}
                          rb={rb}
                          onChange={() => setRefreshKey((k) => k + 1)}
                        />
                      ),
                    }))}
                  />
                </div>
              ),
            },
          ]}
        />
      </div>

      <Modal
        open={addSessionOpen}
        title={t('rencontre.programmation.titre')}
        onCancel={() => { setAddSessionOpen(false); sessionForm.resetFields(); }}
        onOk={() => sessionForm.submit()}
        okText={t('rencontre.programmation.programmer')}
        cancelText={t('actions.annuler')}
        destroyOnClose
      >
        <Form layout="vertical" form={sessionForm} onFinish={ajouterSession}>
          <Form.Item label={t('rencontre.programmation.compagnie')} name="compagnieTrackingId" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={filtreSansAccents}
              optionFilterProp="label"
              options={compagnies
                .filter((c) => c.codePays === 'TGO')
                .map((c) => ({ value: c.organismeTrackingId, label: `${c.code} - ${c.raisonSociale}` }))}
            />
          </Form.Item>

          {/* Indicateur dynamique : la compagnie choisie est-elle déjà programmée ? */}
          {compagnieChoisie && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginTop: -12, marginBottom: 16,
              padding: '8px 12px', borderRadius: 8, fontSize: 12.5,
              background: sessionsCompagnieChoisie.length ? '#fff7ed' : '#f0fdf4',
              border: `1px solid ${sessionsCompagnieChoisie.length ? '#fed7aa' : '#bbf7d0'}`,
              color: sessionsCompagnieChoisie.length ? '#9a3412' : '#166534',
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: sessionsCompagnieChoisie.length ? '#f97316' : '#16a34a'
              }} />
              {sessionsCompagnieChoisie.length
                ? t('rencontre.programmation.dejaProgrammee', { dates: sessionsCompagnieChoisie.map((s) => formatDate(s.dateSession)).join(t('rencontre.programmation.separateurDates')) })
                : t('rencontre.programmation.pasEncoreProgrammee')}
            </div>
          )}

          <Form.Item
            label={t('rencontre.programmation.dateSession')}
            name="dateSession"
            rules={[{ required: true }]}
            extra={
              <span>
                {t('rencontre.programmation.periodeSurlignee', { debut: formatDate(rb.dateDebut), fin: formatDate(rb.dateFin) })}
                {sessions.length > 0 && (
                  <>
                    {' '}<span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: G, verticalAlign: 'middle' }} /> {t('rencontre.programmation.legendePastille')}
                  </>
                )}
              </span>
            }
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              defaultPickerValue={rb.dateDebut ? dayjs(rb.dateDebut) : undefined}
              cellRender={renderCelluleJour}
              disabledDate={(d) => d && (
                d.isBefore(dayjs(rb.dateDebut), 'day') || d.isAfter(dayjs(rb.dateFin), 'day')
              )}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editOpen}
        title={t('rencontre.edition.titre', { reference: rb.reference })}
        onCancel={() => { setEditOpen(false); editForm.resetFields(); }}
        onOk={() => editForm.submit()}
        okText={t('actions.enregistrer')}
        cancelText={t('actions.annuler')}
        width={620}
        destroyOnClose
      >
        <Form layout="vertical" form={editForm} onFinish={enregistrerEdition}>
          <Form.Item label={t('champs.direction')} name="direction" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'RECEPTION', label: t('directionsChoix.RECEPTION') },
                { value: 'DEPLACEMENT', label: t('directionsChoix.DEPLACEMENT') },
              ]}
            />
          </Form.Item>
          <Form.Item label={t('champs.bureauHomologue')} name="bureauHomologueTrackingId" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={filtreSansAccents}
              optionFilterProp="label"
              options={bureaux.map((b) => ({
                value: b.organismeTrackingId,
                label: `${b.codePays} - ${b.raisonSociale}`,
              }))}
            />
          </Form.Item>
          <Form.Item label={t('champs.periode')} name="periode" rules={[{ required: true }]}>
            <DatePicker.RangePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item label={t('champs.observationsGenerales')} name="observationsGenerales">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <PreChecklistModal
        open={preChecklistOpen}
        rbId={id}
        rbReference={rb.reference}
        onClose={() => setPreChecklistOpen(false)}
      />
    </Layout>
  );
}

/* ─── Sous-composants ─── */

const btnPrimary = {
  border: 'none', background: G, color: '#fff', borderRadius: 8,
  padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  display: 'inline-flex', gap: 6, alignItems: 'center',
};
const btnGhost = {
  border: `1px solid ${BORDER}`, background: WHITE, color: INK,
  borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 500,
  cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center',
};
const btnDanger = {
  border: '1px solid #fecaca', background: '#fee2e2', color: '#991b1b',
  borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600,
  cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center',
};
function ButtonGhost({ icon, onClick, children }) {
  return (
    <button onClick={onClick} style={btnGhost}>
      {icon}{children}
    </button>
  );
}

/** Métrique de la bande compacte. Valeur nulle ou zéro → affichage estompé
 *  (« - » pour les montants) afin de ne pas alourdir la page avant saisie. */
function Metric({ label, value, money = false, accent = false }) {
  const vide = !Number(value);
  const affiche = vide
    ? (money ? '-' : '0')
    : (money ? `${fmt(value)} FCFA` : fmt(value));
  return (
    <div style={{ padding: '0 18px', flex: '1 1 auto', minWidth: 110 }}>
      <div style={{
        fontSize: 10.5, fontWeight: 600, color: MUTED,
        letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 2, whiteSpace: 'nowrap'
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap', color: vide ? '#cbd5e1' : accent ? G : INK
      }}>
        {affiche}
      </div>
    </div>
  );
}
